import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  AnimatePresence,
} from "framer-motion";
import {
  ArrowLeft,
  BookOpen,
  Moon,
  Sun,
  Clock,
  Bookmark,
  Library,
  Coffee,
  Heart,
  MessageCircle,
  Sparkles,
  Armchair,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const ease = [0.21, 0.47, 0.32, 0.98];

const TIP_ICONS = {
  bookmark: Bookmark,
  library: Library,
  coffee: Coffee,
  heart: Heart,
  message: MessageCircle,
  sparkles: Sparkles,
  armchair: Armchair,
};

const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.85, ease, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const ThemeToggle = () => {
  const { theme, toggle } = useTheme();
  return (
    <button
      onClick={toggle}
      aria-label="Toggle theme"
      className="relative w-10 h-10 rounded-full flex items-center justify-center border hover:scale-105 transition-transform"
      style={{
        background: "var(--surface-2)",
        borderColor: "var(--border)",
        color: "var(--text)",
      }}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={theme}
          initial={{ rotate: -90, opacity: 0, scale: 0.6 }}
          animate={{ rotate: 0, opacity: 1, scale: 1 }}
          exit={{ rotate: 90, opacity: 0, scale: 0.6 }}
          transition={{ duration: 0.25 }}
          className="flex items-center justify-center"
        >
          {theme === "light" ? (
            <Moon className="w-4 h-4" strokeWidth={2} />
          ) : (
            <Sun className="w-4 h-4" strokeWidth={2} />
          )}
        </motion.span>
      </AnimatePresence>
    </button>
  );
};

const ArticleFigure = ({ src, alt, caption, delay = 0 }) => (
  <FadeIn delay={delay} className="my-14 md:my-20">
    <motion.figure
      className="relative rounded-3xl overflow-hidden"
      whileHover={{ scale: 1.01 }}
      transition={{ duration: 0.6, ease }}
      style={{ boxShadow: "0 24px 60px -24px rgba(120, 70, 40, 0.35)" }}
    >
      <motion.div
        className="aspect-[16/10] overflow-hidden"
        initial={{ scale: 1.08 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.4, ease }}
      >
        <img src={src} alt={alt} className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 book-spine pointer-events-none opacity-60" />
      {caption && (
        <figcaption
          className="mt-4 text-center text-sm italic px-4"
          style={{ color: "var(--text-muted)" }}
        >
          {caption}
        </figcaption>
      )}
    </motion.figure>
  </FadeIn>
);

const ShelfArticlePage = ({ config }) => {
  const {
    images,
    badge,
    title,
    author,
    readTime,
    readTag,
    lede,
    intro,
    quote,
    sections,
    tips,
    closing,
    cta,
    signature,
  } = config;

  const heroRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);

  const { scrollYProgress: pageProgress } = useScroll();
  const progressSpring = useSpring(pageProgress, {
    stiffness: 120,
    damping: 30,
    restDelta: 0.001,
  });

  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(heroProgress, [0, 1], ["0%", "20%"]);
  const heroScale = useTransform(heroProgress, [0, 1], [1, 1.08]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const img = (key) => images[key];

  return (
    <div className="min-h-screen paper-overlay" style={{ background: "var(--bg)" }}>
      <motion.div
        className="fixed top-0 left-0 right-0 h-[3px] z-[60] origin-left"
        style={{
          scaleX: progressSpring,
          background: "linear-gradient(90deg, var(--brand), var(--accent))",
        }}
      />

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "glass py-3" : "py-5"
        }`}
      >
        <div className="max-w-3xl mx-auto px-6 flex items-center justify-between">
          <Link
            to="/shelves"
            className="inline-flex items-center gap-2 text-sm rounded-full px-4 py-2 border transition-colors hover:scale-[1.02]"
            style={{
              borderColor: "var(--border)",
              color: "var(--text-soft)",
              background: "var(--surface)",
            }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to shelves
          </Link>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Link to="/" className="hidden sm:flex" aria-label="PagePeers home">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, var(--brand) 0%, var(--accent) 100%)",
                }}
              >
                <BookOpen
                  className="w-4 h-4"
                  style={{ color: "var(--on-brand)" }}
                  strokeWidth={2.3}
                />
              </div>
            </Link>
          </div>
        </div>
      </header>

      <article>
        <div
          ref={heroRef}
          className="relative h-[72vh] min-h-[480px] max-h-[720px] overflow-hidden"
        >
          <motion.div
            className="absolute inset-0 w-full h-full"
            style={{ y: heroY, scale: heroScale }}
          >
            <img
              src={images.hero}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover"
              fetchPriority="high"
              decoding="sync"
            />
          </motion.div>
          <div
            className="absolute inset-0 pointer-events-none z-[1]"
            style={{
              background:
                "linear-gradient(to bottom, rgba(43,29,20,0.2) 0%, transparent 45%, var(--bg) 100%)",
            }}
          />
          <div className="relative z-10 h-full flex flex-col justify-end max-w-3xl mx-auto px-6 pb-16 pt-32">
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease, delay: 0.15 }}
            >
              <span
                className="inline-block px-3 py-1 rounded-full text-[11px] font-semibold tracking-wider uppercase mb-5"
                style={{
                  background: "var(--surface)",
                  color: "var(--brand)",
                  border: "1px solid var(--border)",
                }}
              >
                {badge}
              </span>
              <h1
                className="font-serif text-4xl md:text-6xl font-medium tracking-tight leading-[1.05] mb-4"
                style={{ color: "var(--text)" }}
              >
                {title}
              </h1>
              <p
                className="text-lg italic font-serif mb-6"
                style={{ color: "var(--text-muted)" }}
              >
                By {author}
              </p>
              <div
                className="flex flex-wrap items-center gap-4 text-sm"
                style={{ color: "var(--text-soft)" }}
              >
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="w-4 h-4" style={{ color: "var(--brand)" }} />
                  {readTime}
                </span>
                <span className="w-1 h-1 rounded-full bg-current opacity-40" />
                <span>{readTag}</span>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 pb-28 -mt-6 relative z-20">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.35 }}
            className="text-xl md:text-2xl leading-relaxed font-serif mb-12"
            style={{ color: "var(--text-soft)" }}
          >
            {lede}
          </motion.p>

          {intro.map((paragraph, i) => (
            <FadeIn key={i} delay={i * 0.05}>
              <p
                className="text-lg leading-[1.85] mb-6"
                style={{ color: "var(--text-soft)" }}
              >
                {paragraph}
              </p>
            </FadeIn>
          ))}

          <motion.blockquote
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease }}
            className="my-16 md:my-20 py-10 px-8 md:px-12 rounded-3xl relative overflow-hidden"
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              boxShadow: "var(--card-shadow)",
            }}
          >
            <motion.div
              className="absolute -top-8 -left-4 w-32 h-32 rounded-full blur-3xl opacity-40"
              style={{ background: "var(--brand-tint)" }}
              animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />
            <p
              className="font-serif text-2xl md:text-3xl leading-snug relative z-10"
              style={{ color: "var(--text)" }}
            >
              {quote}
            </p>
          </motion.blockquote>

          {sections.map((section, i) => (
            <React.Fragment key={i}>
              {section.heading && (
                <FadeIn>
                  <h2
                    className="font-serif text-3xl md:text-4xl font-medium tracking-tight mb-6"
                    style={{ color: "var(--text)" }}
                  >
                    {section.heading}{" "}
                    {section.highlight && (
                      <span className="italic" style={{ color: "var(--brand)" }}>
                        {section.highlight}
                      </span>
                    )}
                  </h2>
                </FadeIn>
              )}
              <FadeIn delay={0.05}>
                <p
                  className="text-lg leading-[1.85] mb-6"
                  style={{ color: "var(--text-soft)" }}
                >
                  {section.body}
                </p>
              </FadeIn>
              {section.figure && (
                <ArticleFigure
                  src={img(section.figure.key)}
                  alt={section.figure.alt}
                  caption={section.figure.caption}
                  delay={0.08}
                />
              )}
            </React.Fragment>
          ))}

          <div className="grid md:grid-cols-2 gap-6 my-14">
            {tips.map((tip, i) => {
              const Icon = TIP_ICONS[tip.icon] || Bookmark;
              return (
                <FadeIn key={tip.title} delay={0.06 + i * 0.06}>
                  <motion.div
                    className="rounded-2xl p-6 h-full"
                    style={{
                      background: i === 0 ? "var(--surface)" : "var(--bg-alt)",
                      border: `1px solid ${i === 0 ? "var(--border)" : "var(--border-soft)"}`,
                    }}
                    whileHover={{ y: -4 }}
                    transition={{ duration: 0.4, ease }}
                  >
                    <Icon
                      className="w-5 h-5 mb-4"
                      style={{ color: i === 0 ? "var(--brand)" : "var(--accent)" }}
                    />
                    <h3
                      className="font-serif text-xl font-medium mb-2"
                      style={{ color: "var(--text)" }}
                    >
                      {tip.title}
                    </h3>
                    <p
                      className="text-sm leading-relaxed"
                      style={{ color: "var(--text-soft)" }}
                    >
                      {tip.text}
                    </p>
                  </motion.div>
                </FadeIn>
              );
            })}
          </div>

          <FadeIn>
            <h2
              className="font-serif text-3xl md:text-4xl font-medium tracking-tight mb-6"
              style={{ color: "var(--text)" }}
            >
              {closing.heading}
            </h2>
            <p
              className="text-lg leading-[1.85] mb-6"
              style={{ color: "var(--text-soft)" }}
            >
              {closing.body}
            </p>
          </FadeIn>

          {closing.figure && (
            <ArticleFigure
              src={img(closing.figure.key)}
              alt={closing.figure.alt}
              caption={closing.figure.caption}
              delay={0.08}
            />
          )}

          <FadeIn delay={0.05}>
            <p
              className="text-lg leading-[1.85] mb-12"
              style={{ color: "var(--text-soft)" }}
            >
              {closing.outro}
            </p>
          </FadeIn>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease }}
            className="rounded-3xl p-8 md:p-10 text-center"
            style={{
              background:
                "linear-gradient(135deg, var(--brand-tint) 0%, var(--bg-alt) 100%)",
              border: "1px solid var(--border)",
            }}
          >
            <p
              className="font-serif text-2xl md:text-3xl font-medium mb-4"
              style={{ color: "var(--text)" }}
            >
              {cta.title}
            </p>
            <p
              className="text-sm mb-8 max-w-md mx-auto"
              style={{ color: "var(--text-soft)" }}
            >
              {cta.desc}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://club.pagepeers.com/register"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-transform hover:scale-[1.02]"
                style={{
                  background: "var(--brand)",
                  color: "var(--on-brand)",
                  boxShadow: "0 6px 20px -6px rgba(194,90,55,0.5)",
                }}
              >
                Join the Club
              </a>
              <Link
                to="/shelves"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm border transition-colors"
                style={{
                  borderColor: "var(--border)",
                  color: "var(--text)",
                  background: "var(--surface)",
                }}
              >
                Explore more shelves
              </Link>
            </div>
          </motion.div>

          <FadeIn delay={0.1}>
            <p
              className="mt-16 text-center text-sm italic"
              style={{ color: "var(--text-muted)" }}
            >
              {signature}
            </p>
          </FadeIn>
        </div>
      </article>
    </div>
  );
};

export default ShelfArticlePage;
